// "Received" for Notice Progress (src/reports/cards/NoticeProgress.jsx) — a notice is RECEIVED for a parcel and year when it was
// SCANNED (a parcel_values row) OR a notice DOCUMENT is filed for that parcel and year. The second arm is what a
// notice filed without scanning leaves (utils/fileWithoutScanning): filed in Notices on that parcel, deliberately
// with no values. It counts as received — not as scanned or audited (Notice Audit lists parcel_values rows only).
//
// A filed notice is found on EITHER link: file_exchange.parcel_id (the document's own parcel) or parcel_documents
// (every parcel the document is linked to — one notice can cover several). Both are read paginated: PostgREST caps
// every response at 1000 rows, silently.
import { supabase } from '../supabase';
import { DOC_KEYS } from './docVocab';
import { ORIGINAL_NOTICE } from './notices';

async function pagedParcelIds(build, label) {
  const ids = new Set();
  for (let from = 0; ; from += 1000) {
    const { data, error } = await build().range(from, from + 999);
    if (error) throw new Error(`${label}: ${error.message}`);
    (data || []).forEach(r => { if (r.parcel_id) ids.add(r.parcel_id); });
    if (!data || data.length < 1000) break;
  }
  return ids;
}

// → { scanned:Set<parcel_id>, filed:Set<parcel_id> } for one tax year ('YYYY'). Throws on a read error — a partial
// read would under-count "received" with nothing on screen to say so.
export async function loadNoticeReceipts(year) {
  const y = String(year);
  const [scanned, viaFx, viaLinks] = await Promise.all([
    pagedParcelIds(() => supabase.from('parcel_values').select('parcel_id').eq('tax_year', y).eq('notice_no', ORIGINAL_NOTICE), 'parcel_values'),
    pagedParcelIds(() => supabase.from('file_exchange').select('parcel_id')
      .eq('folder_key', DOC_KEYS.notices).eq('year_subfolder', y)
      .not('parcel_id', 'is', null).not('filed_at', 'is', null)
      .not('file_name', 'like', '[REMOVED%'), 'file_exchange'),
    pagedParcelIds(() => supabase.from('parcel_documents').select('parcel_id')
      .eq('folder_key', DOC_KEYS.notices).eq('year_subfolder', y), 'parcel_documents'),
  ]);
  return { scanned, filed: new Set([...viaFx, ...viaLinks]) };
}

// 'scanned' | 'filed' (received, not scanned) | null (waiting).
export function noticeReceipt(receipts, parcelId) {
  if (receipts.scanned.has(parcelId)) return 'scanned';
  if (receipts.filed.has(parcelId)) return 'filed';
  return null;
}
